import { HttpClient, HttpEvent, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, interval, Observable, Subject, Subscription, throwError, timer } from 'rxjs';
import { catchError, expand, finalize, map, switchMap, takeUntil } from 'rxjs/operators';

import { environment } from 'src/environments/environment';

import {
  ReplaceStudyFileResponse,
  StudyFile,
  StudyShareFileGet,
} from '../../models/Study/study-file.model';

import { StudySaleSubmission } from 'src/app/models/Study/study-sale-submission';
import {
  DownloadStudyFileDto,
  HorseStudyAcknowledgmentsDto,
  SaleHorseStudyBase,
  Study,
  StudyDraftSubmissionAcknowledgementsRequestDto,
  StudyFileDownloadDetailDto,
  StudyFileDownloadStatusDto,
  StudyFileDownloadStatusQueue,
  StudyListItem,
} from '../../models/Study/study.model';
import { MediaGroup } from '../../models/media/media-group.model';
import {
  Status,
  StudyStatus,
  UpdateStatusFileResponse,
  UpdateStatusResponse,
} from '../../models/status/status.model';
import { StudyMimeType } from '../enums/study-mime-type.enum';
import {
  StudySaleSubmissionModel,
  SubmissionModel,
} from '../models/submission.model';
import { SpinnerService } from './spinner.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ManifestDto } from '../models/manifest-dto';

@Injectable({
  providedIn: 'root',
})
export class StudyService {
  public baseUrl = environment.api2BaseUrl;
  public onError: BehaviorSubject<string> = new BehaviorSubject('');
  private stopPolling$ = new Subject<void>(); // Subject to control polling cancellation
  public serviceParameter = {
    'Content-Type': 'application/json; charset=utf-8',
    Accept: 'application/json',
    Authorization: '',
  };

  public constructor(
    private readonly http: HttpClient,
    private spinnerService: SpinnerService,
    private ngxSpinnerService: NgxSpinnerService
  ) { }

  public getStudies(): Observable<StudyListItem[]> {
    return this.http
      .get<StudyListItem[]>(`${this.baseUrl}/api/Studies/list`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res: any) => {
          if (res.error) {
            throw new Error(res.error);
          }
          return res.body;
        })
      );
  }

  public getStudiesManifest(): Observable<ManifestDto> {
    return this.http
      .get<ManifestDto>(`${this.baseUrl}/api/Studies/list`, {
        withCredentials: false,
        observe: 'response',
        headers: {
          'X-Azure-Cache-Manifest': 'true'
        }
      })
      .pipe(
        map((res: any) => {
          if (res.error) {
            throw new Error(res.error);
          }
          return res.body;
        })
      );
  }

  public getStudiesDownloadToLocalStorage(saleId: number): Observable<DownloadStudyFileDto[]> {
    this.spinnerService.addRequest('downloadToLocalStorage'); // Show spinner
    return this.http
      .get<DownloadStudyFileDto[]>(`${this.baseUrl}/api/StudyFiles/${saleId}/downloadToLocalStorage`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res: any) => {
          if (res.error) {
            throw new Error(res.error);
          }
          return res.body;
        }),
        finalize(() => this.spinnerService.popRequest('downloadToLocalStorage')) // Hide spinner on completion
      );
  }

  public startStudiesDownloadToLocalStorage(saleId: number): Observable<StudyFileDownloadStatusQueue> {
    this.spinnerService.addRequest('downloadToLocalStorage'); // Show spinner
    return this.http
      .post<StudyFileDownloadStatusQueue>(`${this.baseUrl}/api/StudyFiles/${saleId}/downloadToLocalStorage`, null, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res: any) => {
          if (res.error) {
            throw new Error(res.error);
          }
          return res.body;
        }),
        finalize(() => this.spinnerService.popRequest('downloadToLocalStorage')) // Hide spinner on completion
      );
  }

  public getStudiesDownloadStatus(saleId: number): Observable<StudyFileDownloadStatusQueue> {
    return this.http
      .get<StudyFileDownloadStatusQueue>(`${this.baseUrl}/api/StudyFiles/${saleId}/downloadStatus`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res: any) => {
          if (res.error) {
            throw new Error(res.error);
          }
          return res.body;
        }),
      );
  }

  // Start polling and return observable
  public startPollingDownloadStatus(saleId: number): Observable<StudyFileDownloadStatusQueue> {
    // Create a polling observable
    return interval(10000).pipe( // Poll every 10 seconds
      switchMap(() => this.getStudiesDownloadStatus(saleId)), // Replace each interval with the HTTP call
      takeUntil(this.stopPolling$) // Stops polling when triggered
    );
  }

  // Stop polling
  public stopPollingDownloadStatus(): void {
    this.stopPolling$.next(); // Trigger the stop signal
  }

  public CancelDownload(saleId: number): Observable<Boolean> {
    this.spinnerService.addRequest('cancelDownload'); // Show spinner
    return this.http
      .get<Boolean>(`${this.baseUrl}/api/StudyFiles/${saleId}/cancelDownload`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res: any) => {
          if (res.error) {
            throw new Error(res.error);
          }
          return res.body;
        }),
        finalize(() => this.spinnerService.popRequest('cancelDownload')) // Hide spinner on completion
      );
  }

  public IsSaleInProgress(saleId: number): Observable<Boolean> {
    this.spinnerService.addRequest('salesQueueProgressStatus'); // Show spinner
    return this.http
      .get<Boolean>(`${this.baseUrl}/api/StudyFiles/${saleId}/salesQueueProgressStatus`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res: any) => {
          if (res.error) {
            throw new Error(res.error);
          }
          return res.body;
        }),
        finalize(() => this.spinnerService.popRequest('salesQueueProgressStatus')) // Hide spinner on completion
      );
  }

  public getStudy(studyId: number): Observable<Study> {
    return this.http
      .get<Study>(`${this.baseUrl}/api/Studies/${studyId}`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => {
          return res.body;
        })
      );
  }

  public getSaleHorseStudies(
    saleId: number,
    horseId: number,
    isHideSpinner: boolean
  ): Observable<SaleHorseStudyBase> {
    if (saleId && horseId) {
      this.spinnerService.addRequest('loadSaleHorseStudies');
      return this.http
        .get<SaleHorseStudyBase>(
          `${this.baseUrl}/api/Studies/${saleId}/${horseId}/salehorse`,
          {
            withCredentials: false,
            observe: 'response',
          }
        )
        .pipe(
          map((res) => {
            return res.body;
          }),
          finalize(() => {
            this.spinnerService.popRequest('loadSaleHorseStudies');
            if (!isHideSpinner) {
              this.ngxSpinnerService.show();
            }
          })
        );
    }
    return new Observable<SaleHorseStudyBase>();
  }
  public getHorseStudyConfirmation(
    saleId: number,
    horseId: number,
    isHideSpinner: boolean = true
  ): Observable<HorseStudyAcknowledgmentsDto> {
    if (saleId && horseId) {
      this.spinnerService.addRequest('getHorseStudyConfirmation');
      return this.http
        .get<HorseStudyAcknowledgmentsDto>(
          `${this.baseUrl}/api/Studies/${saleId}/${horseId}/acknowledgments`,
          {
            withCredentials: false,
            observe: 'response',
          }
        )
        .pipe(
          map((res) => {
            return res.body;
          }),
          finalize(() => {
            this.spinnerService.popRequest('getHorseStudyConfirmation');
            if (!isHideSpinner) {
              this.ngxSpinnerService.show();
            }
          })
        );
    }
    return new Observable<HorseStudyAcknowledgmentsDto>();
  }
  public getSaleHorseStudyFiles(
    saleId: number,
    horseId: number
  ): Observable<StudyFile[]> {
    if (saleId && horseId) {
      return this.http
        .get<StudyFile[]>(
          `${this.baseUrl}/api/StudyFiles/${saleId}/${horseId}`,
          {
            withCredentials: false,
            observe: 'response',
          }
        )
        .pipe(map((res) => res.body));
    }
    return new Observable<StudyFile[]>();
  }
  public getStudyFiles(studyId: number,showArchived: boolean = true): Observable<StudyFile[]> {
    return this.http
      .get<StudyFile[]>(`${this.baseUrl}/api/StudyFiles/${studyId}?showArchived=${showArchived}`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(map((res) => res.body));
  }
  public getStudyFilesByMimeType(
    saleId: number,
    horseId: number,
    mimeType: StudyMimeType
  ): Observable<StudyFile[]> {
    const type = mimeType as number;
    return this.http
      .post<StudyFile[]>(
        `${this.baseUrl}/api/StudyFiles/mimeType`,
        { saleId: saleId, horseId: horseId, mimeTypeId: type },
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(map((res) => res.body));
  }
  public updateStudyStatus(
    studyId: number,
    statusId: number
  ): Observable<UpdateStatusResponse> {
    const body: UpdateStatusResponse = {
      studyId,
      statusId,
      statusName: '',
    };
    return this.http
      .post<UpdateStatusResponse>(`${this.baseUrl}/api/Studies/status`, body, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => {
          return res.body;
        })
      );
  }

  public updateStudyFileStatuses(
    studyFileIds: number[],
    statusId: number,
    statusMessage: string = '',
    notificationData: any, acknowledgementData: StudyDraftSubmissionAcknowledgementsRequestDto
  ): Observable<{ data: UpdateStatusFileResponse; warnings?: string[] }> {
    const body: UpdateStatusFileResponse = {
      studyFileIds,
      statusId,
      statusName: '',
      statusMessage,
      studyFiles: [],
      submissionNotification: notificationData,
      isLotHasScopeVideoSubmittedAcknowledged: acknowledgementData?.isLotHasScopeVideoSubmittedAcknowledged,
      isLotHasXrayImagesSubmittedAcknowledged: acknowledgementData?.isLotHasXrayImagesSubmittedAcknowledged,
      isStudyUploadedToDifferentLotAcknowledged: acknowledgementData?.isStudyUploadedToDifferentLotAcknowledged
    };

    return this.http
      .post<UpdateStatusFileResponse>(
        `${this.baseUrl}/api/StudyFiles/status`,
        body,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(
        map((res) => {
          const responseBody = res.body as any;

          // Check if there are any warnings in the response
          if (responseBody?.warnings && responseBody.warnings.length > 0) {
            // Log warnings or handle them as needed
            console.warn('Warnings:', responseBody.warnings);

            // Optionally, you could return both data and warnings in a single response object
            return {
              data: responseBody.data,
              warnings: responseBody.warnings,
            };
          }

          // Return just the data if no warnings are present
          return { data: responseBody };
        })
      );
  }

  public updateStudyFileStatusesAutoApprove(
    studyFileIds: number[],
    statusId: number,
    statusMessage: string = '',
    notificationData: any, acknowledgementData: StudyDraftSubmissionAcknowledgementsRequestDto
  ): Observable<{ data: UpdateStatusFileResponse; warnings?: string[] }> {
    const body: UpdateStatusFileResponse = {
      studyFileIds,
      statusId,
      statusName: '',
      statusMessage,
      studyFiles: [],
      submissionNotification: notificationData,
      isLotHasScopeVideoSubmittedAcknowledged: acknowledgementData?.isLotHasScopeVideoSubmittedAcknowledged,
      isLotHasXrayImagesSubmittedAcknowledged: acknowledgementData?.isLotHasXrayImagesSubmittedAcknowledged,
      isStudyUploadedToDifferentLotAcknowledged: acknowledgementData?.isStudyUploadedToDifferentLotAcknowledged
    };

    return this.http
      .post<UpdateStatusFileResponse>(
        `${this.baseUrl}/api/StudyFiles/autoApprove`,
        body,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(
        map((res) => {
          const responseBody = res.body as any;

          // Check if there are any warnings in the response
          if (responseBody?.warnings && responseBody.warnings.length > 0) {
            // Log warnings or handle them as needed
            console.warn('Warnings:', responseBody.warnings);

            // Optionally, you could return both data and warnings in a single response object
            return {
              data: responseBody.data,
              warnings: responseBody.warnings,
            };
          }

          // Return just the data if no warnings are present
          return { data: responseBody };
        })
      );
  }

  public updateStudyDescription(
    studyId: number,
    description: string
  ): Observable<boolean> {
    return this.http
      .post<boolean>(
        `${this.baseUrl}/api/Studies/description`,
        { studyId: studyId, description: description },
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(
        map((res) => {
          return res.body;
        })
      );
  }

  public updateStudySaleDocType(
    studyFileId: number,
    docTypeId: number,
    isReport: boolean
  ): Observable<boolean> {
    return this.http
      .post<boolean>(
        `${this.baseUrl}/api/StudyFiles/doctype`,
        { studyFileId: studyFileId, docTypeId: docTypeId, isReport: isReport },
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(
        map((res) => {
          return res.body;
        })
      );
  }

  public shareStudy(data: any): Observable<boolean> {
    return this.http
      .post<boolean>(`${this.baseUrl}/api/Studies/shareToEmails`, data, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => {
          return res.body;
        })
      );
  }

  public sharedStudyData(
    protectedString: string
  ): Observable<StudyShareFileGet> {
    return this.http
      .get<StudyShareFileGet>(
        `${this.baseUrl
        }/api/Studies/studyShareFiles?protectedString=${encodeURIComponent(
          protectedString
        )}`,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(
        map((res) => {
          return res.body;
        })
      );
  }

  validateShareStudy(protectedString: string): Observable<boolean> {
    return this.http
      .get<boolean>(
        `${this.baseUrl
        }/api/Studies/validateShareStudy?protectedString=${encodeURIComponent(
          protectedString
        )}`,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(
        map((res) => {
          return res.body;
        })
      );
  }

  startValidation(protectedString: string): Observable<boolean> {
    // validate after each minute.
    return this.validateShareStudy(protectedString).pipe(
      expand(() =>
        timer(60000).pipe(
          switchMap(() => this.validateShareStudy(protectedString))
        )
      )
    );
  }

  //public getStudyFiles(studyId: number): Observable<StudyFile[]> {
  //  return this.http
  //    .get<StudyFile[]>(`${this.baseUrl}/api/StudyFiles/${studyId}`, {
  //      withCredentials: false,
  //      observe: 'response',
  //    })
  //    .pipe(map((res) => res.body));
  //}

  public createStudyFile(
    saleId: number,
    horseId: number,
    file: File,
    studyId: number = null,
    isAdditional: boolean = false,
    mediaCategoryId: number = null
  ): Observable<StudyFile> {
    let headers = new HttpHeaders();
    /** In Angular 5, including the header Content-Type can invalidate your request */
    headers.append('Content-Type', 'multipart/form-data');
    headers.append('Accept', 'application/json');

    let formData: FormData = new FormData();
    formData.append('FileUpload', file);
    if (typeof saleId !== 'undefined' && saleId !== null) {
      formData.append('SaleId', saleId.toString());
    }

    if (typeof horseId !== 'undefined' && horseId !== null) {
      formData.append('HorseId', horseId.toString());
    }

    if (studyId) {
      formData.append('StudyId', studyId ? studyId.toString() : '');
    }
    if (typeof isAdditional !== 'undefined') {
      formData.append('IsAdditional', isAdditional.toString());
    }
    if (mediaCategoryId) {
      formData.append('MediaCategoryId', mediaCategoryId.toString());
    }

    return this.http
      .post<StudyFile>(`${this.baseUrl}/api/StudyFiles`, formData, {
        withCredentials: false,
        observe: 'response',
        headers: headers,
      })
      .pipe(
        map((res) => res.body),
        catchError((error: Error) => {
          //
          this.onError.next(error.message);
          return throwError(error);
        })
      );
  }

  public cancelStudyFiles(
    saleId: number,
    horseId: number,
    studyId: number
  ): Observable<boolean> {
    var model = {
      saleId: saleId,
      horseId: horseId,
      studyId: studyId,
    };
    return this.http
      .post<boolean>(`${this.baseUrl}/api/Studies/cancelStudy`, model, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => res.body),
        catchError((error: Error) => {
          //
          this.onError.next(error.message);
          return throwError(error);
        })
      );
  }

  public cancelSubmission(
    saleId: number,
    horseId: number
  ): Observable<boolean> {
    var model = {
      saleId: saleId,
      horseId: horseId,
    };
    return this.http
      .post<boolean>(
        `${this.baseUrl}/api/Studies/cancelStudySubmission`,
        model,
        {
          withCredentials: false,
          observe: 'response',
        }
      )

      .pipe(
        map((res) => res.body),
        catchError((error: Error) => {
          //
          this.onError.next(error.message);
          return throwError(error);
        })
      );
  }

  public cancelStudySubmission(
    submission: StudySaleSubmissionModel,
    studyId: number
  ): Observable<boolean> {
    return this.http
      .post<boolean>(
        `${this.baseUrl}/api/StudyFiles/${studyId}/cancelSubmission`,
        submission,
        {
          withCredentials: false,
          observe: 'response',
        }
      )

      .pipe(
        map((res) => res.body),
        catchError((error: Error) => {
          //
          this.onError.next(error.message);
          return throwError(error);
        })
      );
  }

  public cancelStudySubmissionById(
    saleId: number,
    horseId: number,
    submissionId: number
  ): Observable<boolean> {
    return this.http
      .post<boolean>(
        `${this.baseUrl}/api/Studies/${saleId}/${horseId}/${submissionId}/cancelSubmission`,
        null,
        {
          withCredentials: false,
          observe: 'response',
        }
      )

      .pipe(
        map((res) => res.body),
        catchError((error: Error) => {
          //
          this.onError.next(error.message);
          return throwError(error);
        })
      );
  }

  public setStudyActiveStatusFiles(
    studyId: number,
    isActive: boolean
  ): Observable<boolean> {
    let url = `${this.baseUrl}/api/Studies/${studyId}/archiveStudy`;
    if (isActive) {
      url = `${this.baseUrl}/api/Studies/${studyId}/restoreStudy`;
    }
    return this.http
      .post<boolean>(url, null, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => res.body),
        catchError((error: Error) => {
          //
          this.onError.next(error.message);
          return throwError(error);
        })
      );
  }

  public updateStudyFile(
    studyFileId: number,
    isAdditional: boolean = false,
    mediaCategoryId: number = null
  ): Observable<StudyFile> {
    const body: any = {
      studyFileId,
      isAdditional,
    };

    if (mediaCategoryId) {
      body.mediaCategoryId = mediaCategoryId;
    }
    return this.http
      .post<StudyFile>(`${this.baseUrl}/api/StudyFiles/update`, body, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(map((res) => res.body));
  }

  public replaceStudyFile(
    studyFileId: number,
    mediaCategoryId: number,
    studyId?: number,
    saleId?: number,
    horseId?: number,
  ): Observable<ReplaceStudyFileResponse> {
    const body: any = {
      studyFileId,
      mediaCategoryId,
      studyId,
      saleId,
      horseId,
    };

    return this.http
      .post<ReplaceStudyFileResponse>(`${this.baseUrl}/api/StudyFiles/replace`, body, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(map((res) => res.body));
  }

  public deleteStudyFile(studyFileId: number): Observable<StudyFile> {
    return this.http
      .delete<StudyFile>(`${this.baseUrl}/api/StudyFiles/${studyFileId}`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(map((res) => res.body));
  }

  public getMediaGroups(): Observable<MediaGroup[]> {
    return this.http
      .get<MediaGroup[]>(`${this.baseUrl}/api/Lookups/MediaGroups`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(map((res) => res.body));
  }

  public getStatuses(): Observable<Status[]> {
    return this.http
      .get<Status[]>(`${this.baseUrl}/api/Lookups/StudyStatuses`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(map((res) => res.body));
  }

  public getStudyStatus(studyId: number): Observable<StudyStatus> {
    return this.http
      .get<StudyStatus>(`${this.baseUrl}/api/Studies/${studyId}/status`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(map((res) => res.body));
  }

  public setStudySubmitToSale(
    studySale: StudySaleSubmission
  ): Observable<boolean> {
    return this.http
      .post<boolean>(`${this.baseUrl}/api/Studies/submitToSale`, studySale, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(map((res) => res.body));
  }

  mergeStudies(studyId: number, studyIds: number[]): Observable<boolean> {
    return this.http
      .post<boolean>(
        `${this.baseUrl}/api/Studies/${studyId}/mergeStudies`,
        studyIds,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(map((res) => res.body));
  }
  public onCompleteBatch(data: any): Observable<boolean> {


    return this.http
      .post<boolean>(
        `${this.baseUrl}/api/StudyFiles/completebatch`,
        data,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(
        map((res) => {
          const responseBody = res.body as any;

          // Check if there are any warnings in the response
          if (responseBody?.warnings && responseBody.warnings.length > 0) {
            // Log warnings or handle them as needed
            console.warn('Warnings:', responseBody.warnings);

            // Optionally, you could return both data and warnings in a single response object
            return responseBody;
          }

          // Return just the data if no warnings are present
          return { data: responseBody };
        })
      );
  }

  public getDicomTags(studyFileId: number): Observable<any[]> {
    return this.http
      .get<any[]>(`${this.baseUrl}/api/StudyFiles/${studyFileId}/tags`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(map((res) => res.body));
  }

  public getUserSubmissions(): Observable<SubmissionModel[]> {
    return this.http
      .get<SubmissionModel[]>(`${this.baseUrl}/api/Studies/userSubmissions`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(map((res) => res.body));
  }

  public getDraftSubmissionCount(): Observable<number> {
    return this.http
      .get<number>(`${this.baseUrl}/api/Studies/draftSubmissionCount`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(map((res) => res.body));
  }

  public getSubmissionByStudyId(
    studyId: number
  ): Observable<StudySaleSubmissionModel[]> {
    return this.http
      .get<StudySaleSubmissionModel[]>(
        `${this.baseUrl}/api/StudyFiles/${studyId}/submissions`,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(map((res) => res.body));
  }

  downloadDicomFiles(studyId: number, isPreviewFiles: boolean, cancel$: any): Observable<HttpEvent<Blob>> {
    const url = `${this.baseUrl}/api/studies/${studyId}/downloadDicomFiles?isPreviewFiles=${isPreviewFiles}`;

    return this.http.get(url, {
      responseType: 'blob', // Ensure it's treated as a Blob
      observe: 'events', // For tracking progress and final response
      reportProgress: true
    }).pipe(takeUntil(cancel$));
  }

  downloadDicomFilesForSubmission(studyFileIds: number[], isPreviewFiles: boolean, cancel$: any): Observable<HttpEvent<Blob>> {
    const url = `${this.baseUrl}/api/studies/downloadSubmissionFiles?isPreviewFiles=${isPreviewFiles}`;

    return this.http.post(url, studyFileIds, {
      responseType: 'blob', // Ensure it's treated as a Blob
      observe: 'events', // For tracking progress and final response
      reportProgress: true
    }).pipe(takeUntil(cancel$));
  }

  downloadDicomFilesForShared(protectedString: any, studyFileIds: any, isPreviewFiles: boolean, cancel$: any): Observable<HttpEvent<Blob>> {
    const url = `${this.baseUrl}/api/studies/downloadStudyShareFiles?protectedString=${protectedString}&isPreviewFiles=${isPreviewFiles}`;

    return this.http.post(url, studyFileIds, {
      responseType: 'blob', // Ensure it's treated as a Blob
      observe: 'events', // For tracking progress and final response
      reportProgress: true
    }).pipe(takeUntil(cancel$));
  }

  rotateStudyFile(studyFileId: number, rotate: string): Observable<StudyFile> {
    return this.http
      .get<StudyFile>(
        `${this.baseUrl}/api/StudyFiles/${studyFileId}/${(rotate === 'LEFT'? 'rotateLeft': 'rotateRight')}`,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(map((res) => res.body),
      catchError((error: Error) => {
        this.onError.next(error.message);
        return throwError(error);
      }));
  }
}
